import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseControlComponent } from '@avenir-client-web/base-control';
import { editorDefaultFormats } from '@avenir-client-web/constants';
import { FileService } from '@avenir-client-web/file';
import { LocalizerModule } from '@avenir-client-web/localizer';
import { MessageService } from 'primeng/api';
import { Editor, EditorModule as PrimeEditorModule } from 'primeng/editor';
import Quill from 'quill';
import { throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

interface TextChangeEvent {
  htmlValue: string;
  textValue: string;
}

@Component({
  selector: 'app-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditorComponent),
      multi: true,
    },
    MessageService,
  ],
  imports: [CommonModule, PrimeEditorModule, FormsModule, LocalizerModule],
})
export class EditorComponent
  extends BaseControlComponent
  implements AfterViewInit
{
  @ViewChild(Editor) editor: Editor;

  @Input() formats?: string[] = editorDefaultFormats;

  @Input() viewMode?: boolean = false;

  @Output() textChanged = new EventEmitter<TextChangeEvent>();

  private quillInstance: Quill;

  private fileInput: HTMLInputElement | null = null;

  private readonly fileService = inject(FileService);

  private readonly messageService = inject(MessageService);

  ngAfterViewInit(): void {
    this.addWrapperForEditor();
    if (!this.viewMode) {
      this.setupCustomImageHandler();
    }
  }

  onTextChange({ htmlValue, textValue }: TextChangeEvent): void {
    const hasImgTag = htmlValue?.includes('img');

    super.onValueChange(hasImgTag || textValue ? htmlValue : '');
    this.textChanged.emit({ htmlValue, textValue });
  }

  private addWrapperForEditor(): void {
    const editorEl =
      this.editor.el.nativeElement.getElementsByClassName('ql-blank')[0];

    if (editorEl) {
      const editorWrapper = document.createElement('div');

      editorWrapper.className = 'editor-wrapper';

      editorEl.parentNode.appendChild(editorWrapper);

      editorWrapper.appendChild(editorEl);
    }
  }

  private setupCustomImageHandler(): void {
    setTimeout(() => {
      try {
        this.quillInstance = this.getQuillInstance();

        if (this.quillInstance) {
          this.overrideImageHandler();
          this.setupImagePasteAndDropHandler();
        }
      } catch (error) {
        console.error('Error setting up custom image handler:', error);
      }
    }, 200);
  }

  private setupImagePasteAndDropHandler(): void {
    this.quillInstance.root.addEventListener(
      'paste',
      (event: ClipboardEvent) => {
        const items = event.clipboardData?.items;

        if (!items) {
          return;
        }

        const hasImage = Array.from(items).some(item =>
          item.type.startsWith('image/')
        );

        if (hasImage) {
          event.preventDefault();
          event.stopPropagation();

          Array.from(items).forEach(element => {
            if (element.type.startsWith('image/')) {
              const file = element.getAsFile();

              if (file) {
                this.uploadAndInsertImage(file);
              }
            }
          });
        }
      },
      true
    );

    this.quillInstance.root.addEventListener(
      'drop',
      (event: DragEvent) => {
        const files = event.dataTransfer?.files;

        if (!files || files.length === 0) {
          return;
        }

        const hasImage = Array.from(files).some(file =>
          file.type.startsWith('image/')
        );

        if (!hasImage) return;

        event.preventDefault();
        event.stopPropagation();

        // Attempt to set the cursor position to where the drop happened
        const selection = document.getSelection();
        let range: Range | null = null;

        if (document.caretPositionFromPoint) {
          const pos = document.caretPositionFromPoint(
            event.clientX,
            event.clientY
          );

          if (pos) {
            range = document.createRange();
            range.setStart(pos.offsetNode, pos.offset);
            range.collapse(true);
          }
        } else if ((document as any).caretRangeFromPoint) {
          // Fallback for older browsers
          range = (document as any).caretRangeFromPoint(
            event.clientX,
            event.clientY
          );
        }

        if (selection && range) {
          selection.removeAllRanges();
          selection.addRange(range);
        }

        // Force Quill to update its internal selection state from the native selection
        this.quillInstance.selection.update(Quill.sources.USER);

        Array.from(files).forEach(file => {
          if (file.type.startsWith('image/')) {
            this.uploadAndInsertImage(file);
          }
        });
      },
      true
    );
  }

  private getQuillInstance(): any {
    if ((this.editor as any).quill) {
      return (this.editor as any).quill;
    }

    return null;
  }

  private overrideImageHandler(): void {
    if (!this.quillInstance) {
      return;
    }

    const toolbar = this.quillInstance.getModule('toolbar') as any;

    if (toolbar?.handlers) {
      toolbar.handlers.image = () => {
        this.handleImageButtonClick();
      };
    }
  }

  private handleImageButtonClick(): void {
    if (!this.fileInput) {
      this.fileInput = document.createElement('input');
      this.fileInput.setAttribute('type', 'file');
      this.fileInput.setAttribute('accept', 'image/*');
      this.fileInput.style.display = 'none';
      document.body.appendChild(this.fileInput);

      this.fileInput.addEventListener('change', (event: Event) => {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];

        if (file) {
          this.uploadAndInsertImage(file);
        }

        if (this.fileInput) {
          this.fileInput.value = '';
        }
      });
    }

    this.fileInput.click();
  }

  private uploadAndInsertImage(file: File): void {
    if (!this.quillInstance) {
      return;
    }

    // Upload image
    this.fileService
      .uploadImage(file)
      .pipe(
        map(response => response.data),
        catchError(error => {
          this.messageService.add({
            severity: 'error',
            summary: $localize`editor.imageUpload.error.title`,
            detail: $localize`editor.imageUpload.error.message`,
          });

          return throwError(() => error);
        }),
        finalize(() => {
          this.messageService.clear();
        })
      )
      .subscribe((fileItem: any) => {
        if (fileItem?.id) {
          const imageUrl = this.buildImageUrl(fileItem.id);

          // Use existing selection or append to end if null
          const range = this.quillInstance.getSelection(true) || {
            index: this.quillInstance.getLength(),
          };

          this.quillInstance.insertEmbed(
            range.index,
            'image',
            imageUrl,
            'user'
          );

          // Move cursor after the image
          this.quillInstance.setSelection(range.index + 1, 0);
        }
      });
  }

  private buildImageUrl(fileId: string): string {
    return this.fileService.buildImageCDNUrl(fileId);
  }
}
